import { ref, watch, type Ref } from 'vue';
import type { useShipmentFormStore } from '@/stores/useShipmentFormStore';
import {
  clientKeyForStopField,
  getShipmentFormStopFieldErrors,
  type ShipmentFormStopFieldKey,
} from '@/components/shipment/form/utils/shipment-form-stop-validation';
import type { ShipmentFormStopRow } from '@/stores/useShipmentFormStore';

type FormStore = ReturnType<typeof useShipmentFormStore>;

export function useShipmentFormStopsValidation(options: {
  formStore: FormStore;
  stops: Ref<ShipmentFormStopRow[]>;
}) {
  const clientStopFieldErrors = ref<Record<string, string>>({});

  async function stopsResolver(_opts: { values: Record<string, unknown> }) {
    const errors: Record<string, { message: string }[]> = {};
    const total = options.formStore.stops.length;
    options.formStore.stops.forEach((stop, i) => {
      const fe = getShipmentFormStopFieldErrors(stop, i, total);
      (Object.entries(fe) as [ShipmentFormStopFieldKey, string][]).forEach(
        ([field, msg]) => {
          if (msg) {
            errors[clientKeyForStopField(i, field)] = [{ message: msg }];
          }
        },
      );
    });
    return { errors };
  }

  async function validateStops(): Promise<boolean> {
    clientStopFieldErrors.value = {};
    const r = await stopsResolver({ values: {} });
    const raw = r.errors ?? {};
    const next: Record<string, string> = {};
    for (const [key, list] of Object.entries(raw)) {
      const msg = list?.[0]?.message;
      if (msg) next[key] = msg;
    }
    clientStopFieldErrors.value = next;
    return Object.keys(next).length === 0;
  }

  watch(
    options.stops,
    () => {
      clientStopFieldErrors.value = {};
    },
    { deep: true },
  );

  return {
    clientStopFieldErrors,
    stopsResolver,
    validateStops,
  };
}
